import React from 'react';
import { observer } from 'mobx-react';
import { Box, Flex, Spinner } from 'core/components';
import CoreSelect from 'core/components/select/Select';
import { Field, InputGroup } from 'core/form';
import { BaseAgentRenderer } from 'app/views/synthetics/components/AgentRenderer';
import { SynStore } from 'app/stores/synthetics/synthTypes';
import { FilterState } from '../filter.types';
import { useAgentOptions, AgentOptionData } from './useAgentOptions';

interface AgentFilterInputProps {
  filter: FilterState;
  operator: string;
  $syn?: SynStore;
  autoFocus?: boolean;
}

// Check if operator is an equality operator (= or <>)
function isEqualityOperator(operator: string): boolean {
  return operator === '=' || operator === '<>';
}

// Agent option renderer for Select component
const AgentOptionRenderer = ({ value, label, agentIcon, locationInfo, status }: AgentOptionData) => (
  <Box py="4px">
    <BaseAgentRenderer
      value={value}
      label={label}
      icon={agentIcon}
      locationInfo={locationInfo}
      status={status}
      locationInfoProps={{ ellipsis: true }}
      showLocationInfo
      small
    />
  </Box>
);

// Agent value renderer for Select component (shown when option is selected)
const AgentValueRenderer = (option: AgentOptionData | null) => {
  if (!option) {
    return <span>Select agent...</span>;
  }
  const { value, label, agentIcon, locationInfo, status } = option;
  return (
    <Box py="2px">
      <BaseAgentRenderer
        value={value}
        label={label}
        icon={agentIcon}
        locationInfo={locationInfo}
        status={status}
        locationInfoProps={{ ellipsis: true }}
        showLocationInfo
        small
      />
    </Box>
  );
};

/**
 * Filter value input component for agent dimensions.
 * - Equality operators (=, <>) show a rich dropdown with agent details
 * - Non-equality operators (ILIKE, regex) show a text input for searching by name
 */
const AgentFilterInput = observer(({ filter, operator, $syn, autoFocus }: AgentFilterInputProps) => {
  const showSelect = isEqualityOperator(operator);
  const { options, isLoading } = useAgentOptions($syn, showSelect);

  // Equality operator - use select with rich rendering
  if (showSelect) {
    if (isLoading || !options) {
      return (
        <Flex alignItems="center" gap={1} p={1}>
          <Spinner size={14} />
          <Box color="muted">Loading agents...</Box>
        </Flex>
      );
    }

    const currentValue = filter.filterValue.getValue();
    const handleChange = (value: string) => {
      filter.filterValue.setValue(value);
    };

    return (
      <Box>
        <CoreSelect
          small
          fill
          showFilter
          autoFocus={autoFocus}
          placeholder="Select agent..."
          options={options}
          values={currentValue}
          onChange={handleChange}
          optionFormatter={AgentOptionRenderer}
          valueRenderer={AgentValueRenderer}
        />
      </Box>
    );
  }

  // Non-equality operator (contains, regex) - use text input
  // The filter will be transformed to match agent IDs when applied
  return (
    <Field field={filter.filterValue} showLabel={false} mb={0}>
      <InputGroup small fill autoFocus={autoFocus} placeholder="Search agent name..." />
    </Field>
  );
});

export default AgentFilterInput;
